import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import {
  BatteryAlert as BatteryAlertIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CloudOff as CloudOffIcon,
  Schedule as ScheduleIcon,
  Thermostat as ThermostatIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type {
  ChartSeriesKey,
  DashboardResponse,
  LimitBreach,
  LocationStatus,
  Pour2Limit,
} from '../../../services/pours';
import { formatDateTimeWithZone } from '../../../utils/datetime';
import { channelTypeLabel, isHumidityLocation, isSiteConditionsChannel, isWeatherLocation } from '../../../utils/locationChannels';
import { locationHasMaturity } from '../../../utils/locationMaturity';
import {
  formatMaturityWithIndex,
  isTargetOutsideRange,
  maturityShortLabel,
  targetStrengthText,
} from '../../../utils/maturityDisplay';
import { locationStartsAt } from '../../../utils/onlineStatus';
import { formatAgo, formatDuration, formatIn } from '../../../utils/relativeTime';
import { useNow } from '../../../utils/useNow';
import { buildTimelineEvents, findBreaches } from '../eventTimelineModel';
import { LIMIT_OPERATOR_SYMBOLS, breaksLimit } from '../limitLines';
import { FRESHNESS_TINTS, formatBatteryLevel } from '../locationDisplay';
import { strengthRangeCaption } from '../strengthChart';
import { TargetsCard } from '../TargetsCard';
import { chartRangeMs, defaultChartRange, type ChartRangeKey } from './chartDelta';
import { CollapsibleSection } from './CollapsibleSection';
import {
  isLowerOperator,
  locationChartKinds,
  locationChartNeed,
  locationNames,
  nextTarget,
  type LocationHealth,
} from './mobileDashboardModel';
import { MobileEventList, mobileEventOrder } from './MobileEventList';
import { nextTargetText, strengthOf, worstLocationDifferential, locationAlertWord } from './MobileLocationCard';
import {
  breachDuration,
  breachText,
  channelValue,
  freshnessWord,
  limitNumber,
  limitText,
  locationLabel,
  shortDateTime,
  staleOverText,
  toEpochMs,
} from './mobileText';
import { PhoneChart } from './PhoneChart';
import { buildPhoneLocationChartOptions, PHONE_LOCATION_CHART_HEIGHT, type PhoneLocationChartKind } from './phoneChartOptions';
import { SOFT_CARD_SX } from './PourStatusStrip';
import { ALERT_LOOKS, AlertIcon, PILL_TONES } from './StatusPill';
import { useDashboardModel, type DashboardModel } from './useDashboardModel';
import { useLocationChart } from './useChartData';

export interface MobileLocationViewProps {
  pourId: string;
  data: DashboardResponse;
  pourLimits: ReadonlyArray<Pour2Limit>;
  /** The time of the data (the last successful fetch). */
  refNow: number;
  /** Bumped by the page's polls and refreshes: the chart reloads quietly. */
  refreshTick: number;
  /** The page's selected location (kept across tabs); the first in
   *  Overview order when unset. */
  selectedLocationId?: string;
  onSelectLocation: (locationId: string) => void;
}

const KPI_VALUE_SX = { fontSize: '1.25rem', fontWeight: 650, lineHeight: 1.25, overflowWrap: 'anywhere' } as const;
const SMALL_SX = { fontSize: '0.75rem', lineHeight: 1.35 } as const;
const BODY_SX = { fontSize: '0.875rem', lineHeight: 1.4, overflowWrap: 'anywhere' } as const;
/** A KPI's sub-line: its status word or limit, never below 0.875rem. */
const SUB_SX = { fontSize: '0.875rem', lineHeight: 1.35, overflowWrap: 'anywhere' } as const;

/** A 40px chip whose tap area reaches the 44px row it sits in: white with a
 *  soft shadow, dark when picked. */
export const pickerChipSx = (selected: boolean) => ({
  position: 'relative',
  height: 40,
  fontWeight: selected ? 600 : 500,
  fontSize: '0.875rem',
  borderRadius: '999px',
  bgcolor: selected ? '#1c1f23' : 'background.paper',
  color: selected ? 'common.white' : 'text.primary',
  boxShadow: '0 1px 2px rgba(16,24,40,.06), 0 1px 3px rgba(16,24,40,.05)',
  '& .MuiChip-icon': { color: 'inherit' },
  '&:hover, &.MuiChip-clickable:hover': { bgcolor: selected ? '#1c1f23' : 'grey.100' },
  '&::after': { content: '""', position: 'absolute', top: -2, bottom: -2, left: 0, right: 0 },
}) as const;

/** A card title: small capitals in grey. */
const CARD_TITLE_SX = {
  fontSize: '0.8125rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#4a5056',
} as const;

/** One KPI tile: label, value and a sub-line; `span` 2 for the strength. */
const KpiTile: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  error?: boolean;
  span?: boolean;
  testId?: string;
  children?: React.ReactNode;
}> = ({ label, value, sub, error = false, span = false, testId, children }) => (
  <Box
    data-testid={testId}
    sx={{
      gridColumn: span ? 'span 2' : undefined,
      minHeight: 72,
      p: 1.25,
      borderRadius: '12px',
      bgcolor: error ? PILL_TONES.error.bg : '#f7f8f9',
      minWidth: 0,
    }}
  >
    <Typography sx={{ ...SMALL_SX, color: 'text.secondary' }}>{label}</Typography>
    <Typography component="div" sx={{ ...KPI_VALUE_SX, color: error ? PILL_TONES.error.fg : 'text.primary' }}>
      {value}
    </Typography>
    {sub && (
      <Typography component="div" sx={{ ...SUB_SX, color: error ? PILL_TONES.error.fg : 'text.secondary', fontWeight: error ? 600 : 400 }}>
        {sub}
      </Typography>
    )}
    {children}
  </Box>
);

/** A bad-state chip (battery, signal, sensor issue). */
const WarnChip: React.FC<{ Icon: React.ComponentType<SvgIconProps>; text: string }> = ({ Icon, text }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      minHeight: 32,
      px: 1.25,
      borderRadius: '999px',
      bgcolor: PILL_TONES.warning.bg,
      color: PILL_TONES.warning.fg,
      fontSize: '0.875rem',
      fontWeight: 600,
      maxWidth: '100%',
    }}
  >
    <Icon aria-hidden sx={{ fontSize: 18, flexShrink: 0 }} />
    {text}
  </Box>
);

/** A label / value row of the Logger and Differentials sections. */
const InfoRow: React.FC<{ label: string; value: React.ReactNode; tone?: string }> = ({ label, value, tone }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
    <Typography sx={{ ...BODY_SX, color: 'text.secondary' }}>{label}</Typography>
    <Typography component="div" sx={{ ...BODY_SX, fontWeight: 600, textAlign: 'right', color: tone ?? 'text.primary' }}>
      {value}
    </Typography>
  </Box>
);

/** The range a location's chart opens on: 24h when its readings span more
 *  than 30 hours, else All. */
const defaultRangeFor = (loc: LocationStatus, refNow: number): ChartRangeKey =>
  defaultChartRange(
    toEpochMs(loc.logging_started ?? loc.ea_start ?? loc.scheduled_start),
    toEpochMs(loc.last_read) ?? refNow,
  );

/**
 * The phone dashboard's Location tab: a picker (‹ Select ›) in Overview
 * order, then for the selected location its freshness, the limits it is
 * outside of now, the KPIs, one chart with its kind and range, and closed
 * sections (targets, events, differentials, logger, reading gaps). The
 * freshness, KPIs and breaches come from the summary, so they paint before
 * any chart call; a weather station or a location without a reading makes
 * no chart call. No concrete-start reset on phones.
 */
export const MobileLocationView: React.FC<MobileLocationViewProps> = ({
  pourId,
  data,
  pourLimits,
  refNow,
  refreshTick,
  selectedLocationId,
  onSelectLocation,
}) => {
  const { t } = useTranslation();
  const now = useNow();
  const model = useDashboardModel(data, pourLimits, refNow);
  // The range is kept while stepping through the locations.
  const [range, setRange] = useState<ChartRangeKey | null>(null);
  // The location shown without a pick (the first in Overview order) becomes
  // the page's pick, so a later poll that reorders the Overview never
  // swaps it under the user.
  const fallbackId = model?.orderedLocations[0]?.id;
  const selectedKnown = !!model && model.orderedLocations.some((l) => l.id === selectedLocationId);
  useEffect(() => {
    if (!selectedKnown && fallbackId) onSelectLocation(fallbackId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKnown, fallbackId]);
  if (!model) return null;

  const ordered = model.orderedLocations;
  if (ordered.length === 0) {
    return (
      <Box data-testid="mobile-location-view" sx={{ ...SOFT_CARD_SX, p: 2 }}>
        <Typography sx={BODY_SX}>
          {t('mobile.dashboard.location.noLocations', { defaultValue: 'No monitoring locations are set up for this pour.' })}
        </Typography>
      </Box>
    );
  }
  const location = ordered.find((l) => l.id === selectedLocationId) ?? ordered[0];
  const index = ordered.indexOf(location);
  const health = model.healthById.get(location.id) as LocationHealth;

  return (
    <Box data-testid="mobile-location-view" sx={{ minWidth: 0 }}>
      <Box sx={{ ...SOFT_CARD_SX, borderRadius: '14px', display: 'flex', alignItems: 'center', gap: 0.25, p: 0.5, mb: 1.5, minWidth: 0 }}>
        <IconButton
          aria-label={t('mobile.dashboard.location.previous', { defaultValue: 'Previous location' })}
          disabled={index <= 0}
          onClick={() => onSelectLocation(ordered[index - 1].id)}
          sx={{ width: 44, height: 44, flexShrink: 0 }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Select
          value={location.id}
          onChange={(event) => onSelectLocation(String(event.target.value))}
          size="small"
          inputProps={{ 'aria-label': t('mobile.dashboard.location.picker', { defaultValue: 'Location' }) }}
          renderValue={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <AlertIcon alert={health.alert} />
              <Box sx={{ minWidth: 0, lineHeight: 1.2 }}>
                <Box component="div" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {locationLabel(location)}
                </Box>
                <Box
                  component="div"
                  data-testid="picker-channel"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem', color: 'text.secondary' }}
                >
                  {[
                    t(`mobile.dashboard.channel.${channelTypeLabel(location)}`, { defaultValue: channelTypeLabel(location) }),
                    location.logger_code,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Box>
              </Box>
            </Box>
          )}
          MenuProps={{ slotProps: { paper: { sx: { maxHeight: 400 } } } }}
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiInputBase-input': { minHeight: '28px !important', display: 'flex', alignItems: 'center' },
          }}
        >
          {ordered.map((l) => {
            const h = model.healthById.get(l.id) as LocationHealth;
            return (
              <MenuItem key={l.id} value={l.id} sx={{ minHeight: 48, gap: 1 }}>
                <AlertIcon alert={h.alert} />
                <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {locationLabel(l)}
                </Box>
                <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, color: PILL_TONES[ALERT_LOOKS[h.alert].tone].fg, flexShrink: 0 }}>
                  {locationAlertWord(l, h, t, now)}
                </Box>
              </MenuItem>
            );
          })}
        </Select>
        <IconButton
          aria-label={t('mobile.dashboard.location.next', { defaultValue: 'Next location' })}
          disabled={index >= ordered.length - 1}
          onClick={() => onSelectLocation(ordered[index + 1].id)}
          sx={{ width: 44, height: 44, flexShrink: 0 }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <LocationBody
        key={location.id}
        pourId={pourId}
        data={data}
        model={model}
        location={location}
        health={health}
        pourLimits={pourLimits}
        refNow={refNow}
        now={now}
        refreshTick={refreshTick}
        range={range ?? defaultRangeFor(location, refNow)}
        onRangeChange={setRange}
      />
    </Box>
  );
};

interface LocationBodyProps {
  pourId: string;
  data: DashboardResponse;
  model: DashboardModel;
  location: LocationStatus;
  health: LocationHealth;
  pourLimits: ReadonlyArray<Pour2Limit>;
  refNow: number;
  now: number;
  refreshTick: number;
  range: ChartRangeKey;
  onRangeChange: (range: ChartRangeKey) => void;
}

const EVENTS_SECTION_ID = 'mobile-location-events';

/** One location's view (remounted per location: its sections start closed
 *  and its chart kind starts on temperature). */
const LocationBody: React.FC<LocationBodyProps> = ({
  pourId,
  data,
  model,
  location,
  health,
  pourLimits,
  refNow,
  now,
  refreshTick,
  range,
  onRangeChange,
}) => {
  const { t } = useTranslation();
  const { pour, flags, meta, strengthUnit } = model;
  const tz = pour.timezone_iana;
  const site = isSiteConditionsChannel(location);
  const humidity = isHumidityLocation(location);
  const weatherStation = isWeatherLocation(location);
  const maturityLocation = flags.maturity && locationHasMaturity(location);
  const ended = health.alert === 'ended';
  const showValues = health.alert !== 'scheduled' && !(health.online.awaiting && location.current_temp == null);

  // ---- Chart data (none for a weather station or before a reading) -----
  const needKey = locationChartNeed(location, flags, data.weather != null).join(',');
  const need = useMemo(() => (needKey ? (needKey.split(',') as ChartSeriesKey[]) : []), [needKey]);
  const chart = useLocationChart(pourId, location, need, refreshTick, pour.status, range);
  const chartData = chart.data;
  const kinds = useMemo(
    () => (need.length > 0 ? locationChartKinds(chartData, location, flags, pour) : []),
    [need.length, chartData, location, flags, pour],
  );
  const [kindPick, setKindPick] = useState<PhoneLocationChartKind>('temp');
  const kind: PhoneLocationChartKind = kinds.includes(kindPick) ? kindPick : kinds[0] ?? 'temp';
  const [hiddenByKind, setHiddenByKind] = useState<Partial<Record<PhoneLocationChartKind, string[]>>>({});
  const hiddenList = hiddenByKind[kind];
  const hidden = useMemo(() => new Set(hiddenList ?? []), [hiddenList]);
  const partnerNames = useMemo(() => locationNames(model.locations), [model.locations]);
  const rangeMs = chartRangeMs(range);
  const build = useMemo(
    () =>
      chartData && kinds.length > 0
        ? buildPhoneLocationChartOptions(kind, chartData, {
            rangeMs,
            hidden,
            timezoneIana: tz,
            t,
            locationName: locationLabel(location),
            isHumidity: humidity,
            isSiteHardware: site,
            tempLimits: location.temp_limits ?? null,
            pourLimits,
            differentials: location.differentials ?? null,
            partnerNames,
            meta,
            strengthUnit,
          })
        : null,
    [chartData, kinds.length, kind, rangeMs, hidden, tz, t, location, humidity, site, pourLimits, partnerNames, meta, strengthUnit],
  );

  // ---- Events (from the chart data) -------------------------------------
  const ambientView = !flags.temperature || site || humidity;
  const events = useMemo(
    () =>
      chartData
        ? buildTimelineEvents({
            // Strength targets belong to locations that run maturity.
            chartData: maturityLocation ? chartData : { ...chartData, target: [] },
            ambient: ambientView,
            showStrength: maturityLocation,
            maturityUnit: meta.unit,
            strengthUnit,
            formatTime: (ms) => formatDateTimeWithZone(ms, tz),
          })
        : null,
    [chartData, maturityLocation, ambientView, meta.unit, strengthUnit, tz],
  );
  const alertCount = events ? mobileEventOrder(events).alerts.length : 0;

  // Targets start open (the mockup); the rest start folded.
  const [open, setOpen] = useState<Record<string, boolean>>({ targets: true });
  const toggle = (key: string) => setOpen((o) => ({ ...o, [key]: !o[key] }));
  const showEarlier = () => {
    setOpen((o) => ({ ...o, events: true }));
    setTimeout(() => document.getElementById(EVENTS_SECTION_ID)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  };

  // ---- Limits ----------------------------------------------------------------
  // Each limit of a concrete temperature channel with where the reading
  // stands now ("Within", "Over", "Under"), and its differential limits; no
  // word while the reading is not judged (offline, ended, scheduled).
  const judgedNow = !site && !humidity && ['ok', 'over_limit', 'under_limit', 'sensor', 'delayed'].includes(health.alert);
  const currentTemp = typeof location.current_temp === 'number' ? location.current_temp : null;
  const limitRows: { key: string; text: string; status: { word: string; bad: boolean } | null }[] = [];
  if (!site && !humidity) {
    [...health.limits]
      .sort((a, b) => a.value - b.value)
      .forEach((limit, i) => {
        const lower = isLowerOperator(limit.operator);
        const broken = judgedNow && currentTemp !== null && breaksLimit(currentTemp, limit.operator, limit.value);
        limitRows.push({
          key: `temp-${i}`,
          text: limitText(limit, t),
          status: judgedNow && currentTemp !== null
            ? broken
              ? {
                  word: lower
                    ? t('mobile.dashboard.location.limitUnder', { defaultValue: 'Under' })
                    : t('mobile.dashboard.location.limitOver', { defaultValue: 'Over' }),
                  bad: true,
                }
              : { word: t('mobile.dashboard.location.limitWithin', { defaultValue: 'Within' }), bad: false }
            : null,
        });
      });
    (location.differentials ?? [])
      .filter((d) => d.limit_value != null)
      .forEach((d, i) => {
        limitRows.push({
          key: `diff-${i}`,
          text: t('mobile.dashboard.location.diffLimitRow', {
            defaultValue: '{{limit}} vs {{partner}}',
            limit: limitText({ name: d.limit_name, operator: d.operator, value: d.limit_value as number }, t),
            partner: d.partner_name || '-',
          }),
          status: judgedNow && d.value != null
            ? d.over
              ? { word: t('mobile.dashboard.location.limitOver', { defaultValue: 'Over' }), bad: true }
              : { word: t('mobile.dashboard.location.limitWithin', { defaultValue: 'Within' }), bad: false }
            : null,
        });
      });
  }

  // ---- Freshness banner -------------------------------------------------
  const level = health.online.level;
  const tint = FRESHNESS_TINTS[level];
  const startsAt = locationStartsAt(location);
  let freshText: string;
  if (health.alert === 'scheduled') {
    const when = formatIn(startsAt, now, t);
    freshText = when
      ? t('mobile.dashboard.location.startsWhen', { defaultValue: 'Starts {{when}} ({{at}})', when, at: shortDateTime(startsAt, tz) })
      : freshnessWord('scheduled', t);
  } else if (health.online.awaiting) {
    freshText = t('mobile.dashboard.location.awaitingSince', {
      defaultValue: 'Awaiting first reading · started {{ago}}',
      ago: formatAgo(startsAt, now, t),
    });
  } else if (location.last_read) {
    freshText = level === 'ended'
      ? t('mobile.dashboard.location.endedLastRead', { defaultValue: 'Logging ended · last read {{at}}', at: shortDateTime(location.last_read, tz) })
      : t('mobile.dashboard.location.freshRead', {
          defaultValue: '{{word}} · read {{ago}} ({{at}})',
          word: freshnessWord(level, t),
          ago: formatAgo(location.last_read, now, t),
          at: shortDateTime(location.last_read, tz),
        });
  } else if (level === 'ended') {
    freshText = freshnessWord('ended', t);
  } else {
    freshText = t('mobile.dashboard.location.offlineNoReadings', { defaultValue: 'Offline · No readings received' });
  }
  const FreshIcon = health.alert === 'scheduled' || health.online.awaiting
    ? ScheduleIcon
    : level === 'online' || level === 'ended'
      ? CheckCircleIcon
      : CloudOffIcon;

  // ---- Warning chips ----------------------------------------------------
  const judged = health.alert !== 'ended' && health.alert !== 'scheduled';
  const warnChips: React.ReactNode[] = [];
  if (judged && health.batteryLow && health.batteryPct !== null) {
    warnChips.push(<WarnChip key="battery" Icon={BatteryAlertIcon} text={t('mobile.dashboard.card.battery', { defaultValue: 'Battery {{pct}}%', pct: Math.round(health.batteryPct) })} />);
  }
  if (judged && health.sensorIssue) {
    const said = chartData?.location_details?.location_status_text?.trim();
    warnChips.push(
      <WarnChip
        key="sensor"
        Icon={WarningIcon}
        text={said
          ? t('mobile.dashboard.location.sensorSaid', { defaultValue: 'Sensor issue: {{text}}', text: said })
          : t('mobile.dashboard.location.sensorIssue', { defaultValue: 'Sensor issue reported' })}
      />,
    );
  }

  // ---- Breaches now, with their peak from the readings ------------------
  const breachLine = (b: LimitBreach): string => {
    let run: { start: number; peak: number } | null = null;
    if (b.kind === 'temperature' && chartData) {
      const readings = [...(chartData.temp ?? [])].sort((a, c) => (a[0] ?? 0) - (c[0] ?? 0));
      const runs = findBreaches(readings, { operator: LIMIT_OPERATOR_SYMBOLS[b.operator] ?? b.operator, value: b.limit_value });
      const last = runs[runs.length - 1];
      if (last && last.end == null) run = last;
    }
    const parts = [breachText(b, t, refNow)];
    if (!breachDuration(b, t, refNow) && run) {
      parts[0] = `${parts[0]} ${t('mobile.dashboard.breachFor', { defaultValue: 'for {{duration}}', duration: formatDuration(refNow - run.start, t) })}`;
    }
    parts.push(t('mobile.dashboard.location.breachNow', { defaultValue: 'now {{value}} °C', value: b.value.toFixed(1) }));
    if (run) {
      parts.push(
        isLowerOperator(b.operator)
          ? t('mobile.dashboard.location.breachLowest', { defaultValue: 'lowest {{value}} °C', value: run.peak.toFixed(1) })
          : t('mobile.dashboard.location.breachPeak', { defaultValue: 'peak {{value}} °C', value: run.peak.toFixed(1) }),
      );
    }
    return parts.join(' · ');
  };
  const earlier = Math.max(0, alertCount - health.breaches.filter((b) => b.kind === 'temperature').length);

  // ---- KPIs -------------------------------------------------------------
  const current = chartData?.current;
  const tempBreach = health.breaches.some((b) => b.kind === 'temperature');
  const kpis: React.ReactNode[] = [];
  let hero: React.ReactNode = null;
  if (showValues) {
    const trend = !weatherStation && current?.temp_trend ? current.temp_trend : null;
    const TrendIcon = trend === 'up' ? TrendingUpIcon : trend === 'down' ? TrendingDownIcon : TrendingFlatIcon;
    // "42.3 °C": the number big, its unit small.
    const value = channelValue(location, location.current_temp ?? current?.temperature);
    const split = /^(.*?)\s*(°C|%RH|%)$/.exec(value);
    hero = (
      <Box data-testid="kpi-current" sx={{ mt: 1.5 }}>
        <Typography sx={{ ...SMALL_SX, fontSize: '0.8125rem', color: 'text.secondary' }}>
          {ended ? t('mobile.dashboard.location.last', { defaultValue: 'Last' }) : t('mobile.dashboard.location.current', { defaultValue: 'Current' })}
        </Typography>
        <Typography
          component="div"
          sx={{ fontSize: '2.75rem', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.05, color: tempBreach ? PILL_TONES.error.fg : 'text.primary' }}
        >
          {split ? split[1] : value}
          {split && (
            <Box component="span" sx={{ fontSize: '1.125rem', fontWeight: 500, color: 'text.secondary', ml: 0.5, letterSpacing: 0 }}>
              {split[2]}
            </Box>
          )}
        </Typography>
        {trend && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary', mt: 0.25 }}>
            <TrendIcon aria-hidden sx={{ fontSize: 16 }} />
            {trend === 'up'
              ? t('mobile.dashboard.location.rising', { defaultValue: 'Rising' })
              : trend === 'down'
                ? t('mobile.dashboard.location.falling', { defaultValue: 'Falling' })
                : t('mobile.dashboard.location.stable', { defaultValue: 'Stable' })}
          </Box>
        )}
      </Box>
    );
    const diff = site ? null : worstLocationDifferential(location);
    if (diff) {
      kpis.push(
        <KpiTile
          key="diff"
          testId="kpi-diff"
          label={t('mobile.dashboard.location.differential', { defaultValue: 'Differential' })}
          value={diff.value != null ? `${diff.value.toFixed(1)} °C` : '-'}
          error={diff.over}
          sub={[
            diff.over ? t('mobile.dashboard.card.over', { defaultValue: 'Over' }) : null,
            diff.limit_value != null ? t('mobile.dashboard.card.diffLimit', { defaultValue: 'limit {{limit}} °C', limit: limitNumber(diff.limit_value) }) : null,
            diff.partner_name ? t('mobile.dashboard.card.diffPartner', { defaultValue: 'vs {{partner}}', partner: diff.partner_name }) : null,
          ].filter(Boolean).join(' · ')}
        />,
      );
    }
    const max = location.max_temp ?? current?.max_temperature;
    const maxAt = location.max_temp_at ?? current?.max_temperature_time;
    // The highest upper limit the max broke, named in words (not by the
    // tile's colour alone).
    const maxBroken = !site && !humidity && typeof max === 'number'
      ? health.limits
          .filter((l) => !isLowerOperator(l.operator) && breaksLimit(max, l.operator, l.value))
          .sort((a, b) => b.value - a.value)[0] ?? null
      : null;
    const maxOver = maxBroken !== null;
    if (!weatherStation) {
      kpis.push(
        <KpiTile
          key="max"
          testId="kpi-max"
          label={t('mobile.dashboard.location.max', { defaultValue: 'Max' })}
          value={channelValue(location, max)}
          error={maxOver}
          sub={[
            maxBroken ? t('mobile.dashboard.breachOver', { defaultValue: 'Over {{limit}}', limit: limitText(maxBroken, t) }) : null,
            maxAt
              ? t('mobile.dashboard.location.maxAt', { defaultValue: '{{ago}} · {{at}}', ago: formatAgo(maxAt, now, t), at: shortDateTime(maxAt, tz) })
              : null,
          ].filter(Boolean).join(' · ') || null}
        />,
      );
    }
    if (!site && flags.evaporation) {
      const rate = location.evaporation_now ?? current?.evaporation;
      const limit = (chartData?.evaLimit ?? []).find((l) => !isLowerOperator(l.operator));
      kpis.push(
        <KpiTile
          key="evap"
          testId="kpi-evaporation"
          label={t('mobile.dashboard.location.evaporation', { defaultValue: 'Evaporation' })}
          value={typeof rate === 'number' ? `${rate.toFixed(2)} kg/m²/h` : '-'}
          error={health.evaporationOver}
          sub={[
            health.evaporationOver ? t('mobile.dashboard.card.overLimit', { defaultValue: 'Over limit' }) : null,
            limit ? t('mobile.dashboard.location.evaporationLimit', { defaultValue: 'limit {{limit}}', limit: limitNumber(limit.value) }) : null,
          ].filter(Boolean).join(' · ') || null}
        />,
      );
    }
  }
  if (maturityLocation && health.alert !== 'scheduled') {
    const strength = strengthOf(location, pour, meta, strengthUnit);
    const next = nextTarget(location, meta, { strengthUnit, timezoneIana: tz, ended });
    kpis.push(
      <KpiTile
        key="strength"
        testId="kpi-strength"
        span
        label={strength.label === 'strength'
          ? t('mobile.dashboard.location.strength', { defaultValue: 'Estimated strength' })
          : t('mobile.dashboard.location.maturity', { defaultValue: 'Maturity' })}
        value={<span>{strength.value}</span>}
        sub={strength.label === 'strength' && location.current_ea != null
          ? formatMaturityWithIndex(location.current_ea, meta)
          : null}
      >
        {strength.note && <Typography sx={{ ...SMALL_SX, color: 'text.secondary' }}>{strength.note}</Typography>}
        {next && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
            {next.allReached && <CheckCircleIcon aria-hidden sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />}
            <Typography sx={{ ...BODY_SX, fontWeight: 600 }}>{nextTargetText(next, meta, t)}</Typography>
          </Box>
        )}
        {next && !next.allReached && next.progress !== null && (
          <LinearProgress
            variant="determinate"
            value={Math.round(next.progress * 100)}
            aria-label={t('mobile.dashboard.card.progress', { defaultValue: 'Progress to the next target' })}
            sx={{ height: 4, borderRadius: 2, mt: 0.75 }}
          />
        )}
      </KpiTile>,
    );
  }

  // ---- Chart card -------------------------------------------------------
  const kindLabel = (k: PhoneLocationChartKind): string => {
    switch (k) {
      case 'diff':
        return t('mobile.dashboard.location.kindDiff', { defaultValue: 'Differential' });
      case 'strength':
        return t('mobile.dashboard.location.kindStrength', { defaultValue: 'Strength' });
      case 'maturity':
        return maturityShortLabel(meta);
      case 'eva':
        return t('mobile.dashboard.location.kindEvaporation', { defaultValue: 'Evaporation' });
      default:
        return humidity
          ? t('mobile.dashboard.location.kindHumidity', { defaultValue: 'Humidity' })
          : t('mobile.dashboard.location.kindTemperature', { defaultValue: 'Temperature' });
    }
  };
  let emptyText: string | undefined;
  if (weatherStation) {
    emptyText = t('mobile.dashboard.location.weatherNoChart', { defaultValue: 'Weather station readings are not charted per location' });
  } else if (need.length === 0) {
    emptyText = t('mobile.dashboard.location.noReadingsYet', { defaultValue: 'No readings yet' });
  } else if (chart.error && !chartData) {
    emptyText = chart.error;
  }
  const chartEmpty = need.length === 0 || (!chart.firstLoading && (!build || !build.hasData));
  const strengthCaption = kind === 'strength' && chartData
    ? strengthRangeCaption(chartData.calibrated_range, meta, strengthUnit)
    : '';
  const kindChips = kinds.length > 1 ? (
    <Box role="group" aria-label={t('mobile.dashboard.location.chartKinds', { defaultValue: 'Chart' })} sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.75, mb: 0.5 }}>
      {kinds.map((k) => (
        <Box key={k} sx={{ minHeight: 44, display: 'flex', alignItems: 'center' }}>
          <Chip
            label={kindLabel(k)}
            clickable
            aria-pressed={k === kind}
            onClick={() => setKindPick(k)}
            sx={pickerChipSx(k === kind)}
          />
        </Box>
      ))}
    </Box>
  ) : null;

  // ---- Sections ---------------------------------------------------------
  const summaryTargets = location.targets ?? [];
  const chartTargets = chartData?.target ?? [];
  const targetTotal = chartData ? chartTargets.length : summaryTargets.length;
  const targetReached = chartData ? chartTargets.filter((x) => x.achieved).length : summaryTargets.filter((x) => x.achieved).length;
  const showTargets = maturityLocation && targetTotal > 0;
  const maturityOnlyTarget = chartTargets.some((x) => !targetStrengthText(x.target_strength, strengthUnit));
  const outsideTarget = chartTargets.some((x) => isTargetOutsideRange(x, chartData?.calibrated_range));
  const concreteStart = chartData?.location_details?.ea_started ?? chartData?.ea_start;
  const monitoringStart = chartData?.location_details?.logging_started;

  const diffRows = (location.differentials ?? []).length > 0
    ? (location.differentials ?? []).map((d) => ({
        key: d.partner_id,
        label: t('mobile.dashboard.card.diffPartner', { defaultValue: 'vs {{partner}}', partner: d.partner_name || partnerNames[d.partner_id] || '-' }),
        value: [
          d.value != null ? `${d.value.toFixed(1)} °C` : '-',
          d.limit_value != null ? t('mobile.dashboard.location.diffLimitParen', { defaultValue: '(limit {{limit}} °C)', limit: limitNumber(d.limit_value) }) : null,
          d.over ? t('mobile.dashboard.card.over', { defaultValue: 'Over' }) : null,
        ].filter(Boolean).join(' '),
        over: d.over,
      }))
    : (!site && !humidity ? chartData?.temp_differential_locations ?? [] : []).map((d) => ({
        key: d.location_id,
        label: t('mobile.dashboard.card.diffPartner', { defaultValue: 'vs {{partner}}', partner: d.location_name }),
        value: [
          d.current_diff != null ? `${d.current_diff.toFixed(1)} °C` : '-',
          d.max_diff != null
            ? t('mobile.dashboard.location.diffMax', {
                defaultValue: 'max {{value}} °C {{at}}',
                value: d.max_diff.toFixed(1),
                at: shortDateTime(d.max_diff_time, tz),
              }).trim()
            : null,
        ].filter(Boolean).join(' · '),
        over: false,
      }));

  const ld = chartData?.location_details;
  const gaps = chartData?.reading_interval_issues ?? [];
  const gapCount = Math.max(chartData?.reading_interval_issue_count ?? 0, gaps.length);
  const minutes = (m: number) => String(Number(m.toFixed(1)));

  // The card's left edge: red outside a limit or offline, amber when late
  // or the logger reports a fault (as the Overview card).
  const edge = ['over_limit', 'under_limit', 'offline'].includes(health.alert) || health.evaporationOver
    ? PILL_TONES.error.fg
    : ['delayed', 'sensor'].includes(health.alert)
      ? '#d88c00'
      : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <Box
        data-testid="reading-card"
        sx={{
          ...SOFT_CARD_SX,
          p: 2,
          pl: edge ? '13px' : 2,
          borderLeft: edge ? `4px solid ${edge}` : undefined,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          minWidth: 0,
        }}
      >
      <Box
        data-testid="freshness-banner"
        data-level={level}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          color: tint.fg,
          fontSize: '0.875rem',
          fontWeight: 600,
          minWidth: 0,
          overflowWrap: 'anywhere',
        }}
      >
        <FreshIcon aria-hidden sx={{ fontSize: 18, flexShrink: 0 }} />
        {freshText}
      </Box>

      {warnChips.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>{warnChips}</Box>}

      {health.breaches.length > 0 && (
        <Alert severity="error" icon={<ThermostatIcon />} data-testid="breach-summary" sx={{ '& .MuiAlert-message': { minWidth: 0, flex: 1 } }}>
          {health.breaches.map((b, i) => (
            <Typography key={`${b.kind}-${i}`} sx={{ ...BODY_SX, fontWeight: 600 }}>
              {breachLine(b)}
            </Typography>
          ))}
          {earlier > 0 && (
            <Button color="inherit" onClick={showEarlier} sx={{ minHeight: 44, px: 0, textTransform: 'none', fontWeight: 600, textDecoration: 'underline' }}>
              {t('mobile.dashboard.location.earlierBreaches', {
                count: earlier,
                defaultValue_one: 'Show {{count}} earlier breach',
                defaultValue_other: 'Show {{count}} earlier breaches',
              })}
            </Button>
          )}
        </Alert>
      )}

      {health.staleOver && (
        <Alert severity="warning" icon={<CloudOffIcon />} data-testid="stale-over">
          <Typography sx={{ ...BODY_SX, fontWeight: 600 }}>
            {staleOverText(health.staleOver, t, health.staleOver.at !== null ? formatAgo(health.staleOver.at, now, t) : '')}
          </Typography>
        </Alert>
      )}

      {hero}

      {kpis.length > 0 && (
        <Box
          data-testid="kpi-grid"
          sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}
        >
          {kpis}
        </Box>
      )}
      </Box>

      {showTargets && (
        <CollapsibleSection
          title={t('mobile.dashboard.location.targetsTitle', {
            count: targetTotal,
            reached: targetReached,
            defaultValue_one: 'Targets ({{reached}} of {{count}} reached)',
            defaultValue_other: 'Targets ({{reached}} of {{count}} reached)',
          })}
          open={!!open.targets}
          onToggle={() => toggle('targets')}
        >
          {chartData && chartTargets.length > 0 ? (
            <>
              {maturityOnlyTarget && (
                <Typography sx={{ ...SMALL_SX, color: 'text.secondary', mb: 0.5 }}>
                  {t('mobile.dashboard.location.maturityOnlyNote', { defaultValue: 'A strength of "-": the target is set by maturity only.' })}
                </Typography>
              )}
              {outsideTarget && (
                <Typography sx={{ ...SMALL_SX, color: 'warning.dark', mb: 0.5 }}>
                  {t('mobile.dashboard.location.outsideRangeNote', {
                    defaultValue: 'Outside tested range: outside the tested strength-maturity range, so the strength at this target is a trend estimate.',
                  })}
                </Typography>
              )}
              {/* The desktop card, flattened into this section's card. */}
              <Box sx={{ '& > section.MuiPaper-root': { boxShadow: 'none', bgcolor: 'transparent', p: 0, mb: 0 } }}>
              <TargetsCard
                targets={chartTargets}
                meta={meta}
                strengthUnit={strengthUnit}
                calibratedRange={chartData.calibrated_range ?? null}
                currentMaturity={current?.ea ?? location.current_ea}
                concreteStart={concreteStart}
                monitoringStart={monitoringStart}
                ended={ended}
                timezoneIana={tz}
                calibrationCheckStatus={pour.calibration_check?.status}
                gradeStrength={chartData.product_strength_grade}
                gradeOutsideRange={chartData.grade_outside_range}
              />
              </Box>
            </>
          ) : chart.firstLoading ? (
            <Skeleton variant="rounded" height={120} />
          ) : (
            <Typography sx={{ ...BODY_SX, color: 'text.secondary' }}>
              {chart.error || t('mobile.dashboard.location.targetsUnavailable', { defaultValue: 'The targets show once the readings load.' })}
            </Typography>
          )}
        </CollapsibleSection>
      )}

      {limitRows.length > 0 && (
        <Box data-testid="limits-card" sx={{ ...SOFT_CARD_SX, p: 2, minWidth: 0 }}>
          <Typography component="h3" sx={{ ...CARD_TITLE_SX, mb: 0.5 }}>
            {t('mobile.dashboard.location.limitsTitle', { defaultValue: 'Limits' })}
          </Typography>
          {limitRows.map((row, i) => (
            <Box
              key={row.key}
              data-testid="limit-row"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                py: 1,
                borderTop: i > 0 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ ...BODY_SX, minWidth: 0 }}>{row.text}</Typography>
              {row.status && (
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    px: 1.25,
                    py: '3px',
                    borderRadius: '999px',
                    fontSize: '0.8125rem',
                    fontWeight: 650,
                    bgcolor: row.status.bad ? PILL_TONES.error.bg : '#EEF5E3',
                    color: row.status.bad ? PILL_TONES.error.fg : '#5E8A1E',
                  }}
                >
                  {row.status.word}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Box data-testid="location-chart-card" sx={{ ...SOFT_CARD_SX, p: 2, minWidth: 0 }}>
        <PhoneChart
          title={kinds.length === 1 ? kindLabel(kinds[0]) : undefined}
          newestAt={build?.latest ?? null}
          now={now}
          toolbar={kindChips}
          range={range}
          onRangeChange={onRangeChange}
          showRange={need.length > 0}
          options={build?.options ?? null}
          legend={build?.legend}
          hidden={hidden}
          onToggleSeries={(id) =>
            setHiddenByKind((h) => {
              const list = h[kind] ?? [];
              return { ...h, [kind]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
            })}
          onShowAllSeries={() => setHiddenByKind((h) => ({ ...h, [kind]: [] }))}
          height={PHONE_LOCATION_CHART_HEIGHT}
          loading={need.length > 0 && chart.firstLoading}
          empty={chartEmpty}
          emptyText={emptyText}
          footer={strengthCaption ? (
            <Typography sx={{ ...SMALL_SX, color: 'text.secondary', mt: 1 }} data-testid="strength-range-caption">
              {strengthCaption}
            </Typography>
          ) : undefined}
        />
      </Box>

      {(chartData || (need.length > 0 && chart.firstLoading)) && (
        <CollapsibleSection
          id={EVENTS_SECTION_ID}
          title={alertCount > 0
            ? t('mobile.dashboard.location.eventsBreaches', {
                count: alertCount,
                defaultValue_one: 'Events ({{count}} breach)',
                defaultValue_other: 'Events ({{count}} breaches)',
              })
            : t('mobile.dashboard.location.events', { defaultValue: 'Events' })}
          open={!!open.events}
          onToggle={() => toggle('events')}
        >
          {events ? <MobileEventList events={events} timezoneIana={tz} /> : <Skeleton variant="rounded" height={120} />}
        </CollapsibleSection>
      )}

      {diffRows.length > 0 && (
        <CollapsibleSection
          title={t('mobile.dashboard.location.differentialsTitle', { defaultValue: 'Differentials ({{count}})', count: diffRows.length })}
          open={!!open.diffs}
          onToggle={() => toggle('diffs')}
        >
          {diffRows.map((row) => (
            <InfoRow key={row.key} label={row.label} value={row.value} tone={row.over ? 'error.main' : undefined} />
          ))}
        </CollapsibleSection>
      )}

      <CollapsibleSection
        title={t('mobile.dashboard.location.loggerTitle', { defaultValue: 'Logger' })}
        open={!!open.logger}
        onToggle={() => toggle('logger')}
      >
        <InfoRow label={t('mobile.dashboard.location.loggerNumber', { defaultValue: 'Logger number' })} value={ld?.logger_number || location.logger_code || '-'} />
        <InfoRow
          label={t('mobile.dashboard.location.signal', { defaultValue: 'Signal' })}
          value={location.signal || ld?.signal_strength || '-'}
        />
        <InfoRow
          label={t('mobile.dashboard.location.battery', { defaultValue: 'Battery' })}
          value={health.batteryPct !== null ? `${Math.round(health.batteryPct)}%` : formatBatteryLevel(ld?.battery_level)}
          tone={health.batteryLow ? PILL_TONES.warning.fg : undefined}
        />
        {maturityLocation && (
          <InfoRow
            label={t('mobile.dashboard.location.concreteStart', { defaultValue: 'Concrete start (maturity counted from)' })}
            value={shortDateTime(ld?.ea_started ?? location.ea_start, tz) || '-'}
          />
        )}
        <InfoRow
          label={t('mobile.dashboard.location.monitoringStart', { defaultValue: 'Monitoring start' })}
          value={shortDateTime(ld?.logging_started ?? location.logging_started, tz) || '-'}
        />
        {ld?.location_status_text && (
          <InfoRow label={t('mobile.dashboard.location.loggerStatus', { defaultValue: 'Logger status' })} value={ld.location_status_text} />
        )}
      </CollapsibleSection>

      {maturityLocation && gapCount > 0 && (
        <CollapsibleSection
          title={t('mobile.dashboard.location.gapsTitle', { defaultValue: 'Reading gaps ({{count}})', count: gapCount })}
          open={!!open.gaps}
          onToggle={() => toggle('gaps')}
        >
          <Typography sx={{ ...SMALL_SX, color: 'text.secondary', mb: 0.5 }}>
            {t('mobile.dashboard.location.gapsWarning', {
              defaultValue: 'Readings more than 30 minutes apart in the first 48 hours (or 1 hour after): maturity across these gaps is estimated (ASTM C1074 7.3).',
            })}
          </Typography>
          {gaps.map((gap, i) => (
            <Typography key={`${gap.start}-${i}`} sx={{ ...BODY_SX, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
              {t('mobile.dashboard.location.gapRow', {
                defaultValue: '{{start}} to {{end}} · {{minutes}} min (limit {{limit}} min)',
                start: shortDateTime(gap.start, tz),
                end: shortDateTime(gap.end, tz),
                minutes: minutes(gap.minutes),
                limit: minutes(gap.limit_minutes),
              })}
            </Typography>
          ))}
          {gapCount > gaps.length && (
            <Typography sx={{ ...SMALL_SX, color: 'text.secondary', mt: 0.5 }}>
              {t('mobile.dashboard.location.gapsShown', {
                defaultValue: 'Showing the first {{shown}} of {{total}} gaps.',
                shown: gaps.length,
                total: gapCount,
              })}
            </Typography>
          )}
        </CollapsibleSection>
      )}
    </Box>
  );
};

export default MobileLocationView;
